import { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Bell, BellRing, Loader2, Newspaper, Star } from 'lucide-react';
import { Card } from './ui/card';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import usePushNotifications from '../hooks/usePushNotifications';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NotificationPreferencesCard() {
  const [prefs, setPrefs] = useState({ changelog_emails: true, featured_creator_emails: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const push = usePushNotifications();

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/notifications/preferences`);
        setPrefs(res.data || prefs);
      } catch {
        // fall through with defaults
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = async (key, optOutKey) => {
    const next = !prefs[key];
    setSaving(key);
    try {
      const res = await axios.patch(`${API}/notifications/preferences`, { [optOutKey]: !next });
      setPrefs(res.data || { ...prefs, [key]: next });
      toast.success(next ? 'Subscribed' : 'Unsubscribed');
    } catch {
      toast.error('Update failed');
    } finally {
      setSaving(null);
    }
  };

  const items = [
    {
      key: 'changelog_emails',
      optOutKey: 'notifications_opt_out_changelog',
      icon: Newspaper,
      title: 'Product updates',
      desc: 'Short summary on every major release. No spam, no marketing fluff.',
    },
    {
      key: 'featured_creator_emails',
      optOutKey: 'notifications_opt_out_featured_creator',
      icon: Star,
      title: 'Top Creator of the Week',
      desc: 'See the student we picked to spotlight, with a link to their study notes.',
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      data-testid="notification-preferences-card"
      id="notifications"
    >
      <Card className="f500-card p-6 lg:p-8">
        <div className="flex items-center gap-3 mb-6 pb-4 border-b border-[var(--isp-glass-border)]">
          <Bell className="w-5 h-5 text-[var(--isp-heading)]" strokeWidth={1.75} />
          <div>
            <h2 className="f500-h2 text-xl">Notification Emails</h2>
            <p className="text-sm text-[var(--isp-body)] mt-1" style={{ fontFamily: 'var(--sf-text)' }}>
              Pick what you want to hear about. We email rarely.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-[var(--isp-body)]">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            {/* Browser push subscribe — only when supported & not native Capacitor app */}
            {push.isSupported && (
              <div className="mb-4 border border-[var(--isp-glass-border)] p-4 flex items-start justify-between gap-4" data-testid="push-row">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <BellRing className="w-4 h-4 text-[var(--isp-heading)] mt-0.5 shrink-0" strokeWidth={1.75} />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[var(--isp-heading)]" style={{ fontFamily: 'var(--sf-text)' }}>
                      Browser push notifications
                    </p>
                    <p className="text-xs text-[var(--isp-body)] mt-1 max-w-md">
                      Get a tap-anywhere reminder 5 minutes before your scheduled classes. Used only for time-sensitive prompts — not for marketing.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {push.loading && <Loader2 className="w-3 h-3 animate-spin text-[var(--isp-muted)]" />}
                  <Switch
                    checked={push.isSubscribed}
                    onCheckedChange={async () => {
                      if (push.isSubscribed) {
                        const ok = await push.unsubscribe();
                        if (ok) toast.success('Push notifications off');
                      } else {
                        const ok = await push.subscribe();
                        if (ok) toast.success('Push notifications on');
                        else toast.error('Could not enable push (permission denied?)');
                      }
                    }}
                    disabled={push.loading}
                    data-testid="push-toggle"
                  />
                </div>
              </div>
            )}

          <div className="space-y-0 border border-[var(--isp-glass-border)]">
            {items.map((item, idx) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.key}
                  className={`flex items-start justify-between gap-4 p-4 ${
                    idx > 0 ? 'border-t border-[var(--isp-glass-border)]' : ''
                  }`}
                  data-testid={`pref-row-${item.key}`}
                >
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <Icon className="w-4 h-4 text-[var(--isp-heading)] mt-0.5 shrink-0" strokeWidth={1.75} />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[var(--isp-heading)]" style={{ fontFamily: 'var(--sf-text)' }}>
                        {item.title}
                      </p>
                      <p className="text-xs text-[var(--isp-body)] mt-1 max-w-md">{item.desc}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {saving === item.key && <Loader2 className="w-3 h-3 animate-spin text-[var(--isp-muted)]" />}
                    <Switch
                      checked={!!prefs[item.key]}
                      onCheckedChange={() => toggle(item.key, item.optOutKey)}
                      disabled={saving === item.key}
                      data-testid={`pref-toggle-${item.key}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          </>
        )}
      </Card>
    </motion.div>
  );
}
